import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IdeaIn } from '@/api/generated/schemas';
import { useIdeaCatalogue, useSubmitIdea } from '@/api/queries';
import { fieldErrors, serverFieldErrors } from '@/api/validate';

import { opportunitiesHref } from './ideaFilters';

/** What a person types; the industry id comes from the catalogue. */
const TYPED = IdeaIn.pick({ title: true, description: true, source_urls: true });

/** The typed fields' paths, each shown beside its field; a 422 on any other keeps the banner. */
export const TYPED_FIELDS = Object.keys(TYPED.shape);

/** What a caller may fill in before the person edits and submits. */
export interface IdeaPrefill {
  title: string;
  description: string;
}

/** The Submit an idea dialog's fields, the industries it offers, and the submit itself. */
export function useSubmitIdeaForm(onClose: () => void, prefill?: IdeaPrefill) {
  const catalogue = useIdeaCatalogue();
  const submit = useSubmitIdea();
  const navigate = useNavigate();

  const [title, setTitle] = useState(prefill?.title ?? '');
  const [industryId, setIndustryId] = useState('');
  const [description, setDescription] = useState(prefill?.description ?? '');
  const [sourceUrls, setSourceUrls] = useState<string[]>(['']);
  const setUrl = (index: number, url: string) => setSourceUrls((urls) => urls.map((u, i) => (i === index ? url : u)));
  const removeUrl = (index: number) => setSourceUrls((urls) => (urls.length === 1 ? [''] : urls.filter((_, i) => i !== index)));
  const addUrl = () => setSourceUrls((urls) => [...urls, '']);

  const industries = useMemo(
    () => (catalogue.data?.industries ?? []).filter((i) => i.status === 'approved' && i.is_active),
    [catalogue.data],
  );
  const body = {
    title: title.trim(),
    description: description.trim(),
    industry_id: industryId || null,
    source_urls: sourceUrls.map((u) => u.trim()).filter(Boolean),
  };
  const invalid = Object.keys(fieldErrors(TYPED, body)).length > 0;
  const canSubmit = catalogue.isSuccess && !invalid && !submit.isPending;

  function onSubmit() {
    if (!canSubmit) return;
    submit.mutate(
      body,
      {
        onSuccess: () => {
          onClose();
          // The list, where a new idea shows at once; the matrix plots it once scored.
          navigate(opportunitiesHref({ view: 'list' }));
        },
      },
    );
  }

  return {
    catalogue,
    submit,
    industries,
    canSubmit,
    onSubmit,
    /** A 422's messages by field path, shown beside their fields. */
    serverErrors: serverFieldErrors(submit.error),
    title,
    setTitle,
    industryId,
    setIndustryId,
    description,
    setDescription,
    sourceUrls,
    setUrl,
    removeUrl,
    addUrl,
  };
}

export type SubmitIdeaForm = ReturnType<typeof useSubmitIdeaForm>;
